/**
 * Salesperson strip from an inventory-host (ShedSuite) order payload.
 *
 * The webhook tenant is whoever owns the host subscription (often the
 * manufacturer). The salesperson usually belongs to a dealer org/location
 * on that same host. Invite-target org is resolved separately via dealer map
 * + related orgs — not hardcoded.
 */

export type HostSalesperson = {
  name: string;
  email: string;
  account_id: string;
  dealer_name: string;
  dealer_id: string;
  dealer_city: string;
  dealer_state: string;
};

export type SalespersonInviteRequest = {
  requested_by: string;
  requested_at: string;
};

export type DealSalespersonStatus = "linked" | "missing" | "invite_requested" | "invited";

function str(raw: unknown): string {
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  return "";
}

export function normalizeSalespersonEmail(raw: unknown): string {
  return str(raw).toLowerCase();
}

/** Invite the selling org when mapped; else the location's org; else the webhook tenant. */
export function pickSalespersonInviteOrgId(args: {
  mappedSellingOrgId: string | null;
  locationOrgId: string | null;
  webhookOrgId: string;
}): string {
  const mapped = args.mappedSellingOrgId?.trim() ?? "";
  if (mapped) return mapped;
  const loc = args.locationOrgId?.trim() ?? "";
  if (loc) return loc;
  return args.webhookOrgId;
}

export function hostSalespersonFromUnknown(raw: unknown): HostSalesperson | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const email = normalizeSalespersonEmail(o.email);
  const name = str(o.name);
  const account_id = str(o.account_id);
  const dealer_name = str(o.dealer_name);
  const dealer_id = str(o.dealer_id);
  const dealer_city = str(o.dealer_city);
  const dealer_state = str(o.dealer_state);
  if (!email && !name && !account_id) return null;
  return { name, email, account_id, dealer_name, dealer_id, dealer_city, dealer_state };
}

export function extractHostSalesperson(body: Record<string, unknown>): HostSalesperson | null {
  const email = normalizeSalespersonEmail(body.sold_by_dealer_user_email);
  const name = str(body.sold_by_dealer_user);
  const account_id = str(body.sold_by_dealer_user_account_id);
  if (!email && !name && !account_id) return null;
  return {
    name,
    email,
    account_id,
    dealer_name: str(body.sold_by_dealer_name) || str(body.dealer),
    dealer_id: str(body.sold_by_dealer_id) || str(body.dealer_id),
    dealer_city: str(body.sold_by_dealer_city),
    dealer_state: str(body.sold_by_dealer_state),
  };
}

export function salespersonInviteRequestFromUnknown(raw: unknown): SalespersonInviteRequest | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const requested_by = str(o.requested_by);
  const requested_at = str(o.requested_at);
  if (!requested_by || !requested_at) return null;
  return { requested_by, requested_at };
}

export function deriveSalespersonStatus(args: {
  profileId: string | null;
  inviteRequest: SalespersonInviteRequest | null;
  invitedAt: string | null;
}): DealSalespersonStatus {
  if (args.profileId) return "linked";
  if (args.inviteRequest) return "invite_requested";
  if (args.invitedAt) return "invited";
  return "missing";
}

/** Stable filter key: email, else host account id, else lowercase name. */
export function hostSalespersonKey(h: Pick<HostSalesperson, "email" | "account_id" | "name">): string {
  return h.email || h.account_id || h.name.toLowerCase();
}

/** Stable office/lot filter key: dealer id, else lowercase dealer name. */
export function hostOfficeKey(h: Pick<HostSalesperson, "dealer_id" | "dealer_name">): string {
  return h.dealer_id || h.dealer_name.toLowerCase();
}

export function formatHostOffice(
  h: Pick<HostSalesperson, "dealer_name" | "dealer_city" | "dealer_state">
): string {
  const lot = h.dealer_name.trim();
  const place = [h.dealer_city, h.dealer_state].filter((x) => x.trim()).join(", ");
  if (lot && place) return `${lot} · ${place}`;
  return lot || place;
}

export function formatHostSalespersonName(h: Pick<HostSalesperson, "name" | "email">): string {
  return h.name.trim() || h.email || "";
}
